import React, { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	Grid,
	Card,
	CardContent,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Select,
	MenuItem,
	Button,
	IconButton,
	Chip,
	FormControl,
	InputLabel,
	Alert,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { User, UserType, UserRole } from '../../gql/graphql';
import { useQuery, gql } from '@apollo/client';
import { useShopDetails } from '../../hooks/use-shop';
import { useAuthContext, isRootAdmin, isShopAdmin } from '../../contexts/auth-context';

const GET_SHOP_LOCATIONS = gql`
	query GetUserShopAccess {
		getUserShopAccess {
			_id
			name
			location {
				_id
				address {
					city
				}
			}
		}
	}
`;

interface RolesTabProps {
	userData: Partial<User>;
	updateUserData: (updates: Partial<User>) => void;
}

function RolesTab({ userData, updateUserData }: RolesTabProps) {
	const { shopId } = useShopDetails();
	const { jwtDecoded } = useAuthContext();
	const [locations, setLocations] = useState<any[]>([]);
	const { data: shopData } = useQuery(GET_SHOP_LOCATIONS);

	// Check if current user can edit roles
	const canEditRoles = isRootAdmin(jwtDecoded) || isShopAdmin(jwtDecoded);

	useEffect(() => {
		if (shopData?.getUserShopAccess) {
			// Find the current shop and get its locations
			const currentShop = shopData.getUserShopAccess.find((shop: any) => shop._id === shopId);
			if (currentShop?.location) {
				setLocations(currentShop.location);
			}
		}
	}, [shopData, shopId]);

	const handleAddRole = () => {
		const newRole: UserRole = {
			locationId: locations[0]?._id || '',
			role: UserType.LocationUser,
		};
		const currentRoles = userData.roles || [];
		updateUserData({ roles: [...currentRoles, newRole] });
	};

	const handleRemoveRole = (index: number) => {
		const currentRoles = [...(userData.roles || [])];
		currentRoles.splice(index, 1);
		updateUserData({ roles: currentRoles });
	};

	const handleRoleChange = (index: number, field: keyof UserRole, value: any) => {
		const currentRoles = [...(userData.roles || [])];
		currentRoles[index] = { ...currentRoles[index], [field]: value };
		updateUserData({ roles: currentRoles });
	};

	const getRoleLabel = (role: UserType) => {
		switch (role) {
			case UserType.SuperAdmin:
				return 'Super Admin';
			case UserType.ShopAdmin:
				return 'Shop Admin';
			case UserType.LocationAdmin:
				return 'Location Admin';
			case UserType.LocationBillingUser:
				return 'Billing User';
			case UserType.LocationUser:
				return 'Location User';
			default:
				return role;
		}
	};

	const getRoleColor = (role: UserType): 'error' | 'warning' | 'success' | 'info' | 'default' => {
		switch (role) {
			case UserType.SuperAdmin:
				return 'error';
			case UserType.ShopAdmin:
				return 'warning';
			case UserType.LocationAdmin:
				return 'success';
			case UserType.LocationBillingUser:
				return 'info';
			default:
				return 'default';
		}
	};

	const getLocationName = (locationId?: string | null) => {
		if (!locationId) return 'All Locations';
		const location = locations.find((loc) => loc._id === locationId);
		return location?.address?.city || locationId;
	};

	return (
		<Box>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
				<Box>
					<Typography variant="h6" gutterBottom>
						Roles & Permissions
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{canEditRoles
							? 'Manage user roles and access permissions across locations'
							: 'View user roles and permissions (Read-only)'}
					</Typography>
				</Box>
				{canEditRoles && (
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={handleAddRole}
						disabled={locations.length === 0}
					>
						Add Role
					</Button>
				)}
			</Box>

			{!canEditRoles && (
				<Alert severity="info" sx={{ mb: 3 }}>
					You have read-only access. Only Shop Admins and Super Admins can modify user roles.
				</Alert>
			)}

			{canEditRoles && locations.length === 0 && (
				<Alert severity="warning" sx={{ mb: 3 }}>
					No locations found for this shop. Please add locations first.
				</Alert>
			)}

			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Card>
						<CardContent>
							{!userData.roles || userData.roles.length === 0 ? (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										minHeight: 200,
										bgcolor: 'grey.50',
										borderRadius: 1,
									}}
								>
									<Box sx={{ textAlign: 'center' }}>
										<Typography variant="body1" color="text.secondary" gutterBottom>
											No roles assigned yet
										</Typography>
										<Typography variant="body2" color="text.secondary">
											Click "Add Role" to assign roles to this user
										</Typography>
									</Box>
								</Box>
							) : (
								<Table>
									<TableHead>
										<TableRow>
											<TableCell>Location</TableCell>
											<TableCell>Role</TableCell>
											<TableCell>Permission Level</TableCell>
											<TableCell align="right">Actions</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{userData.roles.map((role, index) => (
											<TableRow key={index}>
												<TableCell>
													{canEditRoles ? (
														<FormControl fullWidth size="small">
															<InputLabel>Location</InputLabel>
															<Select
																value={role.locationId || ''}
																onChange={(e) => handleRoleChange(index, 'locationId', e.target.value)}
																label="Location"
															>
																{locations.map((location) => (
																	<MenuItem key={location._id} value={location._id}>
																		{location.address?.city || location._id}
																	</MenuItem>
																))}
															</Select>
														</FormControl>
													) : (
														<Typography variant="body2">
															{getLocationName(role.locationId)}
														</Typography>
													)}
												</TableCell>
												<TableCell>
													{canEditRoles ? (
														<FormControl fullWidth size="small">
															<InputLabel>Role</InputLabel>
															<Select
																value={role.role}
																onChange={(e) => handleRoleChange(index, 'role', e.target.value as UserType)}
																label="Role"
															>
																<MenuItem value={UserType.LocationUser}>
																	{getRoleLabel(UserType.LocationUser)}
																</MenuItem>
																<MenuItem value={UserType.LocationBillingUser}>
																	{getRoleLabel(UserType.LocationBillingUser)}
																</MenuItem>
																<MenuItem value={UserType.LocationAdmin}>
																	{getRoleLabel(UserType.LocationAdmin)}
																</MenuItem>
																<MenuItem value={UserType.ShopAdmin}>
																	{getRoleLabel(UserType.ShopAdmin)}
																</MenuItem>
																<MenuItem value={UserType.SuperAdmin}>
																	{getRoleLabel(UserType.SuperAdmin)}
																</MenuItem>
															</Select>
														</FormControl>
													) : (
														<Typography variant="body2">
															{getRoleLabel(role.role)}
														</Typography>
													)}
												</TableCell>
												<TableCell>
													<Chip
														label={getRoleLabel(role.role)}
														color={getRoleColor(role.role)}
														size="small"
													/>
												</TableCell>
												<TableCell align="right">
													{canEditRoles && (
														<IconButton
															color="error"
															onClick={() => handleRemoveRole(index)}
															size="small"
														>
															<DeleteIcon />
														</IconButton>
													)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</CardContent>
					</Card>
				</Grid>

				{/* Role Descriptions */}
				<Grid item xs={12}>
					<Card>
						<CardContent>
							<Typography variant="subtitle2" gutterBottom>
								Role Descriptions
							</Typography>
							<Box sx={{ mt: 2 }}>
								<Typography variant="body2" sx={{ mb: 1 }}>
									<strong>Super Admin:</strong> Full system access across all shops and locations
								</Typography>
								<Typography variant="body2" sx={{ mb: 1 }}>
									<strong>Shop Admin:</strong> Full access to all locations within a shop
								</Typography>
								<Typography variant="body2" sx={{ mb: 1 }}>
									<strong>Location Admin:</strong> Administrative access to a specific location
								</Typography>
								<Typography variant="body2" sx={{ mb: 1 }}>
									<strong>Billing User:</strong> Can create and manage orders at a specific location
								</Typography>
								<Typography variant="body2">
									<strong>Location User:</strong> Basic access to view and manage assigned tasks
								</Typography>
							</Box>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</Box>
	);
}

export default RolesTab;
